import { ApiError } from 'api/schema/core/ApiError'
import type { ApiRequestOptions } from 'api/schema/core/ApiRequestOptions'
import { OpenAPI } from 'api/schema/core/OpenAPI'
import { request as __request } from 'api/schema/core/request'
import {
  CleaningZone,
  CleaningZoneRead,
  EventStatistics,
  FileData,
  MainService,
  Mission,
  MissionPointTransferRouteRead,
  MissionRead,
  MissionReadList,
  MissionStatus,
  MissionXyzLayerRead,
  Odyssey,
  RestrictZone,
  SchedulerSettings,
  TransferRouteRead,
} from 'api/schema/index'
import { LineString, MultiLineString, Point, Polygon } from 'geojson'
import chunk from 'lodash.chunk'
import {
  DEFAULT_VIZUALIZATION_SETTINGS,
  GEOTIFF_LAYER_STATUSES,
  MISSION_CALCULATING_STATUSES,
  MISSION_ZONE_TYPES,
  TASK_STATUSES,
  TRANSFER_CALCULATING_STATUSES,
  TRANSFER_TYPE,
} from 'utils/constants'
import {
  ICleaningZone,
  IGeoTiffLayer,
  ILaunchPoint,
  IMission,
  IMissionInfo,
  IPoint,
  IRecordedRoute,
  IRestrictZone,
  IRobotMap,
  IRoute,
  ITransferRoute,
  IVisualizationSettings,
  IZone,
  IZoneTransferRoute,
} from 'utils/interfaces'

import { RecordedRouteRead } from './schema/models/RecordedRouteRead'
import type { OdysseyRead } from './schema/models/OdysseyRead'
import type { PaginatedOdysseyReadList } from './schema/models/PaginatedOdysseyReadList'

/**
 * GET unified-events / gausium-normalized-*: if `robot_ids` is present (incl. `[]`),
 * `robot_sn` is dropped so the wire request matches backend (robot_ids wins; no spurious 403 on sn).
 * Non-empty scopes always go as compact CSV `robot_ids` (same style as odyssey list) so large fleets
 * do not blow the query string with hundreds of repeated `robot_id=` params.
 */
function toNormalizedRobotScopeQuery(params: Record<string, unknown>): Record<string, unknown> {
  const { robot_ids, robot_sn, ...rest } = params as {
    robot_ids?: number[]
    robot_sn?: string
    [key: string]: unknown
  }
  const q: Record<string, unknown> = { ...rest }
  if (robot_ids !== undefined) {
    if (robot_ids.length) {
      q.robot_ids = robot_ids.join(',')
    } else {
      q.robot_ids = ''
    }
  } else if (robot_sn) {
    q.robot_sn = robot_sn
  }
  return q
}

function buildDeltaCursor(rows: Array<{ updated_at?: string; event_id?: string }>): string {
  let bestUpdated = ''
  let bestEventId = ''
  for (const row of rows) {
    const u = String(row.updated_at ?? '')
    const e = String(row.event_id ?? '')
    if (!u || !e) continue
    if (u > bestUpdated || (u === bestUpdated && e > bestEventId)) {
      bestUpdated = u
      bestEventId = e
    }
  }
  return bestUpdated && bestEventId ? `${bestUpdated}|${bestEventId}` : ''
}

const normalizeCleaningZone = (zoneRaw: CleaningZoneRead | CleaningZone): ICleaningZone => {
  return {
    id: 'cleaning_' + zoneRaw.id,
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: (zoneRaw.geometry as Polygon).coordinates,
    },
    properties: {
      zone_type: zoneRaw.is_recorded ? MISSION_ZONE_TYPES.RECORDED : MISSION_ZONE_TYPES.CLEANING,
      name: zoneRaw.name || '',
      description: zoneRaw.description || '',
      zone_id: zoneRaw.id,
      cleaning_type: zoneRaw.cleaning_type ?? 1,
      id_on_map: 'cleaning_' + zoneRaw.id,
      status: (zoneRaw as CleaningZoneRead).status
        ? ((zoneRaw as CleaningZoneRead).status as MISSION_CALCULATING_STATUSES)
        : MISSION_CALCULATING_STATUSES.NEW,
      readyForCalculation: (zoneRaw as CleaningZoneRead).is_ready_to_start_calculation
        ? (zoneRaw as CleaningZoneRead).is_ready_to_start_calculation
        : true,
      length: (zoneRaw as CleaningZoneRead).cleaning_route_geometry_length || undefined,
      area: (zoneRaw as CleaningZoneRead).geometry_area || undefined,
      executionTime: (zoneRaw as CleaningZoneRead).cleaning_route_execution_time || undefined,
      route_id: (zoneRaw as CleaningZoneRead).route_id || undefined,
      is_recorded: (zoneRaw as CleaningZoneRead).is_recorded || undefined,
      is_border_cleaning: (zoneRaw as CleaningZoneRead).is_border_cleaning || undefined,
      createdAt: (zoneRaw as CleaningZoneRead).created_at,
      updatedAt: (zoneRaw as CleaningZoneRead).updated_at,
      cleaning_speed:
        zoneRaw.cleaning_speed !== undefined && zoneRaw.cleaning_speed !== null ? zoneRaw.cleaning_speed : 0.5,
      transport_speed:
        zoneRaw.transport_speed !== undefined && zoneRaw.transport_speed !== null ? zoneRaw.transport_speed : 0.7,
      equipment_setup: (zoneRaw as any).equipment_setup || undefined,
      setup: (zoneRaw as any).setup || undefined,
    },
  }
}

const normalizeRecordedRoute = (routeRaw: RecordedRouteRead | any): IRecordedRoute => {
  const geometry = routeRaw.cleaning_route_geometry || routeRaw.route_geometry
  return {
    id: routeRaw.id,
    type: 'Feature',
    geometry: {
      type: 'MultiLineString',
      coordinates: geometry?.coordinates ? chunk(geometry.coordinates[0], 30000) : [],
    },
    properties: {
      name: routeRaw.name || (routeRaw.filename ? routeRaw.filename.replace('.json', '') : '') || '',
      zone_id: routeRaw.id,
      id_on_map: 'recorded_route_' + routeRaw.id,
      length: routeRaw.route_length || undefined,
      filename: routeRaw.filename || undefined,
      createdAt: routeRaw.created_at,
      updatedAt: routeRaw.updated_at,
      recordedAt: routeRaw.recorded_at || undefined,
      userId: routeRaw.user_id,
      groupId: routeRaw.group_id,
      facilityId:
        routeRaw.facility_id !== undefined
          ? routeRaw.facility_id
          : routeRaw.facility !== undefined
          ? routeRaw.facility
          : null,
      localizationMode: routeRaw.localization_mode,
      lidarMapId: routeRaw.lidar_map_id !== undefined ? routeRaw.lidar_map_id : null,
      lidarMapName: routeRaw.lidar_map_name !== undefined ? routeRaw.lidar_map_name : null,
      backwardGeometry: routeRaw.route_backward_geometry || null,
      forwardGeometry: routeRaw.route_forward_geometry || null,
    },
  }
}

const normalizeRestrictZone = (zoneRaw: RestrictZone): IRestrictZone => {
  return {
    id: 'restrict_' + zoneRaw.id,
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: (zoneRaw.geometry as Polygon).coordinates,
    },
    properties: {
      zone_type: MISSION_ZONE_TYPES.RESTRICT,
      name: zoneRaw.name || '',
      description: zoneRaw.description || '',
      zone_id: zoneRaw.id,
      id_on_map: 'restrict_' + zoneRaw.id,
    },
  }
}

const normalizeCleaningRoute = (cleaningZoneRaw: CleaningZoneRead | any): IRoute => {
  const geometry = cleaningZoneRaw.cleaning_route_geometry || cleaningZoneRaw.route_geometry
  return {
    id: 'cleaning_route_' + cleaningZoneRaw.id,
    type: 'Feature',
    geometry: {
      type: 'MultiLineString',
      coordinates: geometry?.coordinates ? chunk(geometry.coordinates[0], 30000) : [],
    },
    properties: {
      zone_id: cleaningZoneRaw.id,
      id_on_map: 'cleaning_' + cleaningZoneRaw.id,
      name:
        cleaningZoneRaw.name || (cleaningZoneRaw.filename ? cleaningZoneRaw.filename.replace('.json', '') : '') || '',
      backwardGeometry: cleaningZoneRaw.cleaning_route_backward_geometry || null,
      forwardGeometry: cleaningZoneRaw.cleaning_route_forward_geometry || null,
    },
  }
}

const normalizeTransferRoute = (transferRouteRaw: TransferRouteRead, transportSpeed: number): IZoneTransferRoute => {
  const length = transferRouteRaw.geometry_length || transferRouteRaw.user_geometry_length || undefined
  const executionTime = length && transportSpeed > 0 ? length / transportSpeed : undefined

  return {
    id: 'transfer_route_' + transferRouteRaw.id,
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: transferRouteRaw.geometry?.coordinates || [],
    },
    userGeometry: {
      type: 'LineString',
      coordinates: transferRouteRaw.user_geometry?.coordinates || transferRouteRaw.geometry?.coordinates || [],
    },
    properties: {
      id: transferRouteRaw.id,
      zone_id_from: transferRouteRaw.cleaning_zone_from_id,
      zone_id_to: transferRouteRaw.cleaning_zone_to_id,
      id_on_map: 'transfer_route_' + transferRouteRaw.id,
      type: TRANSFER_TYPE.MIDDLE,
      status: transferRouteRaw.status
        ? (transferRouteRaw.status as TRANSFER_CALCULATING_STATUSES)
        : TRANSFER_CALCULATING_STATUSES.NEW,
      length,
      executionTime,
    },
  }
}

const normalizeRoute = (transferRouteRaw: MissionPointTransferRouteRead, transportSpeed: number): ITransferRoute => {
  const length = transferRouteRaw.geometry_length || transferRouteRaw.user_geometry_length || undefined
  const executionTime = length && transportSpeed > 0 ? length / transportSpeed : undefined

  return {
    id: 'mission_route_' + transferRouteRaw.id,
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: transferRouteRaw.geometry?.coordinates || transferRouteRaw.user_geometry?.coordinates || [],
    },
    userGeometry: {
      type: 'LineString',
      coordinates: transferRouteRaw.user_geometry?.coordinates || transferRouteRaw.geometry?.coordinates || [],
    },
    properties: {
      id: transferRouteRaw.id,
      type: transferRouteRaw.point_type === 'start' ? TRANSFER_TYPE.START : TRANSFER_TYPE.FINISH,
      status: transferRouteRaw.status
        ? (transferRouteRaw.status as TRANSFER_CALCULATING_STATUSES)
        : TRANSFER_CALCULATING_STATUSES.NEW,
      length,
      executionTime,
    },
  }
}

const normalizeLaunchPoints = (cleaningZonesRaw: CleaningZoneRead[]): ILaunchPoint[] => {
  const result = cleaningZonesRaw.filter(zone => zone.starting_point).map(zone => normalizeLaunchPoint(zone))
  return result as ILaunchPoint[]
}

const normalizeLaunchPoint = (cleaningZoneRaw: CleaningZoneRead | CleaningZone): ILaunchPoint | undefined => {
  if (cleaningZoneRaw.starting_point?.coordinates) {
    return {
      id: 'launch_point_' + cleaningZoneRaw.id,
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: cleaningZoneRaw.starting_point?.coordinates,
      },
      properties: {
        zone_id: cleaningZoneRaw.id,
        id_on_map: 'cleaning_' + cleaningZoneRaw.id,
      },
    }
  }
}

const normalizeMissionInfo = (missionRaw: MissionRead | MissionReadList): IMissionInfo => {
  const isList = 'results' in missionRaw
  const mission = isList ? missionRaw.results[0] : missionRaw

  return {
    id: mission.id,
    name: mission.name || '',
    location: mission.location || null,
    description: mission.description || '',
    createdAt: mission.created_at,
    updatedAt: mission.updated_at,
    status: mission.status as MISSION_CALCULATING_STATUSES,
    robot_type: mission.robot_type || '',
    transport_speed: mission.transport_speed || 0,
    cleaning_speed: mission.cleaning_speed || 0,
    overall_execution_time: (mission as any).overall_execution_time,
    overall_length: (mission as any).overall_length,
    overall_cleaned_area: (mission as any).overall_cleaned_area,
    resource_profile: (mission as any).resource_profile ?? undefined,
    facility_id: (mission as any).facility_id ?? (mission as any).facility ?? null,
    cannot_delete_reason: (mission as any).cannot_delete_reason || null,
  }
}

const normalizeGeoTiffLayer = (layerRaw: MissionXyzLayerRead | undefined): IGeoTiffLayer | undefined => {
  if (!layerRaw) {
    return
  }
  const bbox: Polygon | undefined =
    layerRaw.bbox && layerRaw.bbox.coordinates
      ? {
          type: 'Polygon',
          coordinates: layerRaw.bbox.coordinates,
        }
      : undefined
  const geoTiffLayer = layerRaw
    ? {
        id: layerRaw.id,
        bbox,
        urlTiles: layerRaw.file_tiles || undefined,
        name: layerRaw.name || '',
        status: (layerRaw.status as GEOTIFF_LAYER_STATUSES) || GEOTIFF_LAYER_STATUSES.NEW,
      }
    : undefined
  return geoTiffLayer
}

const normalizeRobotMap = (mapRaw: any): IRobotMap => {
  return {
    id: mapRaw.id,
    name: mapRaw.name || '',
    robot_sn: mapRaw.robot_sn || '',
    facility_id: mapRaw.facility_id,
    map_id: mapRaw.map_id,
    folder: mapRaw.folder,
    status: (mapRaw.status as GEOTIFF_LAYER_STATUSES) || GEOTIFF_LAYER_STATUSES.NEW,
    file_tiles: mapRaw.file_tiles || undefined,
    file_size: mapRaw.file_size || undefined,
    file_md5: mapRaw.file_md5 || undefined,
    bbox: mapRaw.bbox ? (typeof mapRaw.bbox === 'string' ? JSON.parse(mapRaw.bbox) : mapRaw.bbox) : undefined,
    metadata: mapRaw.metadata,
    createdAt: mapRaw.created_at,
    usage_count: mapRaw.usage_count,
    used_in_missions: mapRaw.used_in_missions,
    is_base_map: mapRaw.is_base_map || false,
    is_edited: mapRaw.is_edited || false,
    is_processing: mapRaw.is_processing || false,
  }
}

const normalizePoint = (
  pointRaw?: { type?: 'Point'; coordinates?: Array<number> } | null,
  start: boolean = true
): IPoint | undefined => {
  if (pointRaw && pointRaw.coordinates) {
    const id = start ? 'mission_start_point' : 'mission_finish_point'
    return {
      id,
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: pointRaw?.coordinates,
      },
      properties: {
        id_on_map: id,
      },
    }
  }
}

let statusIsPending = false

export const MainApi = {
  fetchMissions: async (
    page?: number,
    pageSize?: number,
    robotType?: string,
    search?: string,
    groupId?: number,
    dispatchToRobot?: boolean
  ): Promise<{ results: IMissionInfo[]; count: number }> => {
    const missions: { results?: any[]; total?: number } = dispatchToRobot
      ? await __request(OpenAPI, {
          method: 'GET',
          url: '/api/main/mission/',
          query: {
            ordering: ['-id'],
            page,
            page_size: pageSize,
            search,
            robot_type: robotType,
            dispatch_to_robot_type: robotType,
            group: groupId,
          },
        })
      : await MainService.mainMissionList(['-id'], page, pageSize, search, robotType, groupId)
    if (!missions.results) {
      return { results: [], count: 0 }
    }
    const normalizedMissions = missions.results.map(missionRaw => normalizeMissionInfo(missionRaw))
    return { results: normalizedMissions, count: missions.total || 0 }
  },
  createMission: async (name: string, robot_type: string, description: string): Promise<Mission> => {
    const mission = await MainService.mainMissionCreate({
      name,
      robot_type,
      description,
    })
    return mission
  },
  fetchMission: async (id: number): Promise<IMission> => {
    const missionRaw = await MainService.mainMissionRetrieve(id)
    const mission = normalizeMissionInfo(missionRaw)
    const status = missionRaw.status
      ? (missionRaw.status as MISSION_CALCULATING_STATUSES)
      : MISSION_CALCULATING_STATUSES.NEW
    const isReadyForCalculation = missionRaw.is_ready_to_start_calculation

    const cleaningZones = missionRaw.cleaning_zones.map(zoneRaw => normalizeCleaningZone(zoneRaw))
    const restrictZones = missionRaw.restrict_zones.map(zoneRaw => normalizeRestrictZone(zoneRaw))
    const cleaningRoutes = missionRaw.cleaning_zones
      .filter(zoneRaw => zoneRaw.cleaning_route_geometry)
      .map(zoneRaw => normalizeCleaningRoute(zoneRaw))
    const transferRoutes = missionRaw.transfer_routes.map(transferRouteRaw =>
      normalizeTransferRoute(transferRouteRaw, missionRaw.transport_speed || 0.7)
    )
    const launchPoints = normalizeLaunchPoints(missionRaw.cleaning_zones)
    const geoTiffLayer = normalizeGeoTiffLayer(missionRaw.xyz_layers[missionRaw.xyz_layers.length - 1])
    const startingPoint = normalizePoint(missionRaw.starting_point, true)
    const finishingPoint = normalizePoint(missionRaw.finishing_point, false)
    const pointRoutes = missionRaw.points_transfer_routes.map(routeRaw =>
      normalizeRoute(routeRaw, missionRaw.transport_speed || 0.7)
    )
    const visualizationSettings = { ...DEFAULT_VIZUALIZATION_SETTINGS, ...missionRaw.visualization_settings }
    return {
      status,
      mission,
      cleaningZones,
      restrictZones,
      cleaningRoutes,
      launchPoints,
      isReadyForCalculation,
      transferRoutes,
      geoTiffLayer,
      startingPoint,
      finishingPoint,
      pointRoutes,
      visualizationSettings,
    }
  },
  changeMissionName: async (id: number, name: string): Promise<Mission> => {
    const missionRaw = await MainService.mainMissionPartialUpdate(id, {
      name,
    })
    return missionRaw
  },

  changeLocationName: async (id: number, location: string | null): Promise<Mission> => {
    const missionRaw = await MainService.mainMissionPartialUpdate(id, {
      location,
    })
    return missionRaw
  },

  changeMissionRobotType: async (id: number, robot_type: string): Promise<Mission> => {
    const missionRaw = await MainService.mainMissionPartialUpdate(id, {
      robot_type,
    })
    return missionRaw
  },

  changeMissionSpeed: async (id: number, transport_speed: number, cleaning_speed: number): Promise<MissionRead> => {
    // First update the mission speeds
    await MainService.mainMissionPartialUpdate(id, {
      transport_speed: transport_speed,
      cleaning_speed: cleaning_speed,
    })

    // Then retrieve the full mission with updated zones
    const missionRaw = await MainService.mainMissionRetrieve(id)
    return missionRaw
  },

  changeMissionVisualSettings: async (id: number, visualSettings: IVisualizationSettings): Promise<Mission> => {
    const missionRaw = await MainService.mainMissionPartialUpdate(id, {
      visualization_settings: visualSettings,
    })
    return missionRaw
  },
  changeMissionStartPoint: async (id: number, startingPoint: Point | null): Promise<IPoint | undefined> => {
    const missionRaw = await MainService.mainMissionPartialUpdate(id, {
      starting_point: startingPoint,
    })
    return normalizePoint(missionRaw.starting_point)
  },
  changeMissionFinishPoint: async (id: number, finishingPoint: Point | null): Promise<IPoint | undefined> => {
    const missionRaw = await MainService.mainMissionPartialUpdate(id, {
      finishing_point: finishingPoint,
    })
    return normalizePoint(missionRaw.finishing_point)
  },

  updateOdysseySchedule: async (id: number, scheduler_settings: SchedulerSettings): Promise<Odyssey> => {
    const OdysseyRaw = await MainService.mainOdysseyPartialUpdate(id, {
      scheduler_settings: scheduler_settings,
      status: TASK_STATUSES.NEW,
    })
    return OdysseyRaw
  },

  deleteMission: async (id: number): Promise<void> => {
    await MainService.mainMissionDestroy(id)
  },

  copyMission: async (id: number): Promise<void> => {
    await MainService.mainMissionCopy(id)
  },

  createRestrictZone: async (
    missionId: number,
    name: string,
    description: string,
    geometry: Polygon
  ): Promise<IZone> => {
    const zone = await MainService.mainRestrictZoneCreate({
      name,
      description,
      mission_id: missionId,
      geometry,
    })
    return normalizeRestrictZone(zone)
  },
  deleteRestrictZone: async (id: number): Promise<void> => {
    await MainService.mainRestrictZoneDestroy(id)
  },
  patchRestrictZone: async (
    zoneId: number,
    missionId: number,
    name: string,
    description: string,
    geometry?: Polygon
  ): Promise<IZone> => {
    const zone = await MainService.mainRestrictZonePartialUpdate(zoneId, {
      name,
      description,
      mission_id: missionId,
      geometry,
    })
    return normalizeRestrictZone(zone)
  },
  createCleaningZone: async (
    missionId: number,
    name: string,
    description: string,
    geometry: Polygon,
    routeID: number | undefined,
    launchPointGeometry?: Point,
    cleaningSpeed?: number,
    transportSpeed?: number,
    is_border_cleaning?: boolean,
    zoneRoute?: MultiLineString | undefined,
    equipment_setup?: { turbine_speed?: number; front_dust_suppression?: boolean },
    is_recorded?: boolean | false,
    setup?: {
      swath_sequence_mode?: number
      borders_num?: number
      main_thresholds?: { max_edge_angle?: number; max_edge_angle_for_borders?: number }
    },
    backwardGeometry?: MultiLineString | undefined
  ): Promise<{ zone: ICleaningZone; launchPoint: ILaunchPoint | undefined }> => {
    const zone = await MainService.mainCleaningZoneCreate({
      name,
      description,
      mission_id: missionId,
      geometry,
      starting_point: launchPointGeometry,
      route_id: routeID,
      cleaning_speed: cleaningSpeed,
      transport_speed: transportSpeed,
      is_border_cleaning: is_border_cleaning,
      cleaning_route_geometry: zoneRoute,
      cleaning_route_backward_geometry: backwardGeometry,
      equipment_setup: equipment_setup,
      is_recorded: is_recorded,
      setup: setup,
    } as any)
    return { zone: normalizeCleaningZone(zone), launchPoint: normalizeLaunchPoint(zone) }
  },

  changeCleaningTypeCleaningZone: async (id: number, cleaning_type: number): Promise<ICleaningZone> => {
    const zoneRaw = await MainService.mainCleaningZoneCleaningTypeUpdate(id, {
      cleaning_type,
    })
    return normalizeCleaningZone(zoneRaw)
  },

  changeCleaningZoneSpeed: async (
    id: number,
    cleaning_speed: number,
    transport_speed: number
  ): Promise<CleaningZone> => {
    const zoneRaw = await MainService.mainCleaningZonePartialUpdate(id, {
      cleaning_speed,
      transport_speed,
    })
    return zoneRaw
  },

  getRecordedRoute: async (filename: string): Promise<MultiLineString> => {
    const routeRaw = await MainService.mainGetRouteFromFile(filename)
    return routeRaw
  },

  patchCleaningZone: async (
    zoneId: number,
    missionId: number,
    name: string,
    description: string,
    routeID: number | undefined,
    geometry?: Polygon,
    cleaning_route_geometry?: MultiLineString,
    launchPoint?: Point | null,
    cleaningSpeed?: number,
    transportSpeed?: number,
    is_border_cleaning?: boolean,
    equipment_setup?: { turbine_speed?: number; front_dust_suppression?: boolean },
    setup?: {
      swath_sequence_mode?: number
      borders_num?: number
      main_thresholds?: { max_edge_angle?: number; max_edge_angle_for_borders?: number }
    },
    backwardGeometry?: MultiLineString | undefined,
    invert_orientations?: boolean
  ): Promise<{ zone: IZone; launchPoint: ILaunchPoint | undefined; cleaning_route_geometry: IRoute | undefined }> => {
    const updateData: any = {
      name,
      description,
      route_id: routeID,
      mission_id: missionId,
      geometry,
      cleaning_route_geometry,
      cleaning_route_backward_geometry: backwardGeometry,
      starting_point: launchPoint,
      is_border_cleaning,
    }

    if (cleaningSpeed !== undefined) {
      updateData.cleaning_speed = cleaningSpeed
    }
    if (transportSpeed !== undefined) {
      updateData.transport_speed = transportSpeed
    }
    if (equipment_setup !== undefined) {
      updateData.equipment_setup = equipment_setup
    }
    if (setup !== undefined) {
      updateData.setup = setup
    }
    if (invert_orientations !== undefined) {
      updateData.invert_orientations = invert_orientations
    }

    const zone = await MainService.mainCleaningZonePartialUpdate(zoneId, updateData)

    return {
      zone: normalizeCleaningZone(zone),
      launchPoint: normalizeLaunchPoint(zone),
      cleaning_route_geometry: normalizeCleaningRoute(zone),
    }
  },
  fetchCleaningZone: async (zoneId: number) => {
    const zone = await MainService.mainCleaningZoneRetrieve(zoneId)
    return { zone: normalizeCleaningZone(zone), route: normalizeCleaningRoute(zone) }
  },
  deleteCleaningZone: async (id: number): Promise<void> => {
    await MainService.mainCleaningZoneDestroy(id)
  },

  setOccupiedSide: async (id: number, side: 'left' | 'right'): Promise<void> => {
    await __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/cleaning-zone/{id}/set-occupied-side/',
      path: {
        id: id,
      },
      body: { side },
    })
  },
  copyCleaningZoneToMission: async (id: number, missionId: number): Promise<void> => {
    await MainService.mainCleaningZoneCopy(id, missionId)
  },

  moveZone: async (id: number, up: boolean): Promise<void> => {
    await MainService.mainMoveZone(id, up)
  },

  startCalculatingRoutes: async (missionId: number): Promise<void> => {
    if (statusIsPending) {
      statusIsPending = false
    }
    await MainService.mainMissionStartCalculationCreate(missionId)
  },
  fetchCalculationStatus: async (missionId: number): Promise<MissionStatus | undefined> => {
    if (statusIsPending) {
      return
    }
    statusIsPending = true
    const status = await MainService.mainMissionGetStatusRetrieve(missionId)
    if (!statusIsPending) {
      return
    }
    statusIsPending = false
    return status
  },
  startCalculatingRoute: async (zoneId: number): Promise<void> => {
    if (statusIsPending) {
      statusIsPending = false
    }
    await MainService.mainCleaningZoneStartCalculationCreate(zoneId)
  },
  startCalculatingTransferRoute: async (transferRouteId: number): Promise<void> => {
    if (statusIsPending) {
      statusIsPending = false
    }
    await MainService.mainTransferRouteStartCalculationCreate(transferRouteId)
  },
  startCalculatingMissionPointRoute: async (transferRouteId: number): Promise<void> => {
    if (statusIsPending) {
      statusIsPending = false
    }
    await MainService.mainMissionPointTransferRouteStartCalculationCreate(transferRouteId)
  },
  patchTransferRoute: async (id: number, geometry?: LineString): Promise<void> => {
    await MainService.mainTransferRoutePartialUpdate(id, {
      user_geometry: geometry,
    })
  },
  patchTransferRouteWithRecorded: async (id: number, routeId: number): Promise<void> => {
    await __request(OpenAPI, {
      method: 'PATCH',
      url: `/api/main/transfer-route/${id}/`,
      body: {
        route_id: routeId,
      },
    })
  },
  deleteTransferRoute: async (id: number): Promise<void> => {
    await MainService.mainTransferRouteDestroy(id)
  },
  patchMissionPointTransferRoute: async (id: number, geometry?: LineString): Promise<void> => {
    await MainService.mainMissionPointTransferRoutePartialUpdate(id, {
      user_geometry: geometry,
    })
  },
  patchMissionPointTransferRouteWithRecorded: async (id: number, routeId: number): Promise<void> => {
    await __request(OpenAPI, {
      method: 'PATCH',
      url: `/api/main/mission-point-transfer-route/${id}/`,
      body: {
        route_id: routeId,
      },
    })
  },
  deleteMissionPointTransferRoute: async (id: number): Promise<void> => {
    await MainService.mainMissionPointTransferRouteDestroy(id)
  },
  createLayer: async (missionId: number, name: string): Promise<IGeoTiffLayer> => {
    const layer = await MainService.mainMissionXyzLayerCreate({ name, mission_id: missionId })
    return { id: layer.id, urlTiles: undefined, name, status: GEOTIFF_LAYER_STATUSES.NEW }
  },
  uploadLayerFile: async (layerId: number, file: File): Promise<void> => {
    await MainService.mainMissionXyzLayerUploadFileCreate(layerId, { file })
  },
  linkRobotMapToLayer: async (layerId: number, robotMapId: number): Promise<IGeoTiffLayer | undefined> => {
    const response: any = await __request(OpenAPI, {
      method: 'POST',
      url: `/api/main/mission-xyz-layer/${layerId}/link-robot-map/`,
      body: { robot_map_id: robotMapId },
    })
    return normalizeGeoTiffLayer(response)
  },
  deleteLayer: async (layerId: number): Promise<void> => {
    await MainService.mainMissionXyzLayerDestroy(layerId)
  },
  async fetchGeotiffFiles(): Promise<FileData[]> {
    try {
      const response = await MainService.mainListGeoTiffFiles()
      return response
    } catch (error) {
      console.error('Error fetching GeoTIFF files:', error)
      throw error
    }
  },

  async fetchRecordedRoutesList(): Promise<IRecordedRoute[]> {
    try {
      const response = await MainService.mainListRecordedRoutes()
      return response ? response.map((routeRaw: any) => normalizeRecordedRoute(routeRaw)) : []
    } catch (error) {
      console.error('Error fetching recorded routes files:', error)
      throw error
    }
  },

  async fetchRecordedRoutes(
    page?: number,
    pageSize?: number,
    search?: string
  ): Promise<{ results: IRecordedRoute[]; count: number }> {
    try {
      const response = await MainService.mainRecordedRouteList(page, pageSize, search)
      return {
        results: response.results ? response.results.map((routeRaw: any) => normalizeRecordedRoute(routeRaw)) : [],
        count: response.total || 0,
      }
    } catch (error) {
      console.error('Error fetching recorded routes:', error)
      throw error
    }
  },
  async fetchRecordedRoute(id: number): Promise<IRecordedRoute> {
    try {
      const response = await MainService.mainRecordedRouteRetrieve(id)
      return normalizeRecordedRoute(response)
    } catch (error) {
      console.error('Error fetching recorded route:', error)
      throw error
    }
  },
  async deleteRecordedRouteItem(id: number): Promise<void> {
    try {
      await MainService.mainRecordedRouteDestroy(id)
      return
    } catch (error) {
      console.error('Error deleting recorded route:', error)
      throw error
    }
  },

  copyRecordedRoute: async (id: number): Promise<void> => {
    try {
      await __request(OpenAPI, {
        method: 'POST',
        url: '/api/main/recorded-route/{id}/copy/',
        path: {
          id: id,
        },
      })
    } catch (error) {
      console.error('Error copying recorded route:', error)
      throw error
    }
  },
  renameRouteRecordedZone: async (id: number, name: string): Promise<void> => {
    try {
      await MainService.mainRecordedRoutePartialUpdate(id, { name })
      return
    } catch (error) {
      console.error('Error renaming recorded route:', error)
      throw error
    }
  },

  updateRecordedRouteGeometry: async (
    id: number,
    geometry: MultiLineString,
    invertOrientations: boolean = false
  ): Promise<void> => {
    try {
      await MainService.mainRecordedRoutePartialUpdate(id, {
        route_geometry: geometry,
        invert_orientations: invertOrientations,
      } as any)
    } catch (error) {
      console.error('Error updating recorded route geometry:', error)
      throw error
    }
  },

  uploadLayerFileByName: async (layerId: number, filename: string): Promise<void> => {
    await MainService.mainMissionXyzLayerUploadFileByName(layerId, { filename })
  },
  fetchEventStatistics: async (params?: {
    start_date?: string
    end_date?: string
    robot_sn?: number
    mission_id?: number
    task_id?: number
    page?: number
    page_size?: number
    ordering?: string
  }) => {
    return await MainService.getEventStatistics(params)
  },
  fetchOdysseyEventStatistics: async (params?: {
    start_date?: string
    end_date?: string
    robot_sn?: number
    mission_id?: number
    task_id?: number
    page?: number
    page_size?: number
    ordering?: string
  }) => {
    return await MainService.getOdysseyEventStatistics(params)
  },
  fetchFacilities: async (
    page?: number,
    pageSize?: number,
    search?: string,
    options?: { favoritesFirst?: boolean; favoritesOnly?: boolean }
  ): Promise<{ results: any[]; count: number }> => {
    const query: Record<string, any> = {}
    if (page) query.page = page
    if (pageSize) query.page_size = pageSize
    if (search) query.search = search
    if (options?.favoritesFirst) query.favorites_first = '1'
    if (options?.favoritesOnly) query.favorites_only = '1'
    query.ordering = '-id'

    const response = (await __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/facility/',
      query,
    })) as any
    return { results: response.results || [], count: response.total || response.count || 0 }
  },

  createFacility: async (data: {
    name: string
    facility_type?: string
    street_address?: string
    city?: string
    country?: string
    state_province?: string
    postal_code?: string
    robots_ids?: number[]
    geo_point?: { type: 'Point'; coordinates: number[] } | null
  }): Promise<any> => {
    return await __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/facility/',
      body: data,
    })
  },

  deleteFacility: async (id: number): Promise<void> => {
    await __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/main/facility/{id}/',
      path: { id },
    })
  },

  patchFacility: async (
    id: number,
    data: {
      name?: string
      facility_type?: string
      street_address?: string
      city?: string
      country?: string
      state_province?: string
      postal_code?: string
      robots_ids?: number[]
      geo_point?: { type: 'Point'; coordinates: number[] } | null
    }
  ): Promise<any> => {
    return await __request(OpenAPI, {
      method: 'PATCH',
      url: '/api/main/facility/{id}/',
      path: { id },
      body: data,
    })
  },

  /** Facility detail; includes optional `geo_point` (MR-FACILITY-GEO). */
  fetchFacility: async (id: number): Promise<any> => {
    return await __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/facility/{id}/',
      path: { id },
    })
  },

  fetchLotModels: async (): Promise<any[]> => {
    const response = (await __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/lot-models/',
      query: { limit: 1000 },
    })) as any
    return response.results || []
  },

  fetchMaintenancePoints: async (facility_id?: number): Promise<any[]> => {
    const query: Record<string, any> = {}
    if (facility_id) query.facility = facility_id
    const response = (await __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/maintenance-point/',
      query,
    })) as any
    return response.results || []
  },

  /** Mirrored GS executable tasks for facility (DB); requires Celery sync. */
  fetchFacilityGausiumExecutableTasks: async (
    facilityId: number
  ): Promise<
    Array<{
      id: number
      robot_id: number
      robot_sn?: string
      vendor_task_uuid?: string
      task_name?: string
      vendor_map_id?: string
      map_name?: string
      gausium_map_id?: number | null
      last_seen_at?: string | null
    }>
  > => {
    const res = await __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/facility/{id}/gausium-executable-tasks/',
      path: { id: facilityId },
    })
    return Array.isArray(res) ? res : []
  },

  fetchScenarios: async (
    page?: number,
    pageSize?: number,
    search?: string,
    robot_type?: string,
    group_id?: number,
    facility_id?: number,
    mission_id?: number,
    dispatchToRobot?: boolean
  ): Promise<{ results: any[]; count: number }> => {
    const query: Record<string, any> = {}
    if (pageSize != null) {
      query.page_size = pageSize
      query.page = page != null && page >= 1 ? page : 1
    }
    if (search) query.search = search
    if (robot_type) {
      query.robot_type = robot_type
      if (dispatchToRobot) query.dispatch_to_robot_type = robot_type
    }
    if (group_id) query.group = group_id
    if (facility_id) query.facility = facility_id
    if (mission_id != null) query.mission = mission_id
    query.ordering = '-id'

    const response: any = await __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/scenario/',
      query,
    })
    const list = response?.results ?? (Array.isArray(response) ? response : [])
    const count = Number(response?.total ?? response?.count ?? 0) || 0
    return { results: list, count }
  },

  fetchScenario: async (id: number): Promise<any> => {
    return await __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/scenario/{id}/',
      path: { id },
    })
  },

  createScenario: async (name: string, facility_id?: number): Promise<any> => {
    return await __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/scenario/',
      body: { name, facility_id },
    })
  },

  patchScenarios: async (
    id: number,
    data: {
      name?: string
      missions_ids?: number[]
      robot_type?: string
      maintenance?: any
      facility_id?: number
      settings?: {
        internet?: boolean
        charger?: boolean
        battery?: number
        clean_water_level?: number
        dirty_water_level?: number
      } | null
      mission_equipment_setups?: Record<string, { front_lights?: boolean }>
    }
  ): Promise<any> => {
    return await __request(OpenAPI, {
      method: 'PATCH',
      url: '/api/main/scenario/{id}/',
      path: { id },
      body: data,
    })
  },

  copyScenario: async (id: number): Promise<void> => {
    await __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/scenario/{id}/copy/',
      path: { id },
    })
  },

  deleteScenario: async (id: number): Promise<void> => {
    await __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/main/scenario/{id}/',
      path: { id },
    })
  },

  fetchGausiumTaskReports: async (params: { start_date: string; end_date: string; robot_sn?: string }) => {
    const res = await MainService.getGausiumTaskReports(params)
    return res as { total: number; results: EventStatistics[]; gausium_robot_sns: string[] }
  },

  /** Temporary PNG URL from Gausium generateRobotTaskReportPng (client uses e.g. <img src={uri}>). */
  fetchGausiumTaskReportPngUri: async (params: { robot_sn: string; report_id: string; language_code?: string }) => {
    const query: Record<string, string> = {
      robot_sn: params.robot_sn,
      report_id: params.report_id,
    }
    if (params.language_code) query.language_code = params.language_code
    return (await __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/gausium-task-report-png/',
      query,
    })) as { uri: string }
  },

  /** PNG bytes via backend (proxied from vendor URL); use blob + <a download> for Save dialog. */
  downloadGausiumTaskReportPngBlob: async (params: { robot_sn: string; report_id: string }): Promise<Blob> => {
    const search = new URLSearchParams({
      robot_sn: params.robot_sn,
      report_id: params.report_id,
      download: '1',
    })
    const path = `/api/main/gausium-task-report-png/?${search.toString()}`
    const base = (OpenAPI.BASE || '').replace(/\/$/, '')
    const url = `${base}${path}`
    const token =
      typeof OpenAPI.TOKEN === 'function'
        ? await (OpenAPI.TOKEN as (o?: unknown) => Promise<string | undefined> | string | undefined)({})
        : OpenAPI.TOKEN
    const headers: HeadersInit = { Accept: 'image/png, application/json;q=0.9, */*;q=0.8' }
    if (token) (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`
    const res = await fetch(url, { headers })
    const reqStub: ApiRequestOptions = {
      method: 'GET',
      url: '/api/main/gausium-task-report-png/',
      query: { robot_sn: params.robot_sn, report_id: params.report_id, download: '1' },
    }
    if (!res.ok) {
      const text = await res.text()
      let body: unknown
      try {
        body = JSON.parse(text)
      } catch {
        body = { detail: text || res.statusText }
      }
      throw new ApiError(
        reqStub,
        { url, ok: false, status: res.status, statusText: res.statusText, body },
        res.statusText
      )
    }
    return res.blob()
  },

  /**
   * Schedule templates for the calendar (GET `/api/main/odyssey/`).
   * Date window is applied on the client; `robotIds` only narrows the fleet.
   */
  fetchOdysseys: async (robotIds?: number[]): Promise<OdysseyRead[]> => {
    if (robotIds && robotIds.length === 0) return []
    const pageSize = 1000
    const all: OdysseyRead[] = []
    let page = 1
    for (;;) {
      const res = (await __request(OpenAPI, {
        method: 'GET',
        url: '/api/main/odyssey/',
        query: {
          page,
          page_size: pageSize,
          ordering: '-id',
          ...(robotIds?.length ? { robot_id: robotIds.join(',') } : {}),
        },
      })) as PaginatedOdysseyReadList
      const rows = res.results ?? []
      all.push(...rows)
      if (!res.page_next || rows.length < pageSize) break
      page += 1
      if (page > 20) break
    }
    return all
  },

  /** Normalized event rows for dashboard, scheduler, and history (GET `/api/main/unified-events/`). */
  fetchUnifiedEvents: async (params: {
    start_date: string
    end_date: string
    robot_sn?: string
    /** If set, takes precedence over robot_sn (see toNormalizedRobotScopeQuery). */
    robot_ids?: number[]
    source?: 'autonomics' | 'gausium_task_reports' | 'event_statistics' | 'odyssey_statistics'
    cache_only?: boolean
  }) => {
    const res = (await __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/unified-events/',
      query: toNormalizedRobotScopeQuery({ ...params } as Record<string, unknown>) as any,
    })) as any
    return res as {
      total: number
      results: any[]
      timings_ms?: Record<string, number>
      snapshot_cursor?: string
    }
  },

  /** Light polling: GS gap-fill progress for this user + date window (small JSON). */
  fetchGausiumNormalizedSyncStatus: async (params: {
    start_date: string
    end_date: string
    robot_sn?: string
    /** If set, takes precedence over robot_sn (see toNormalizedRobotScopeQuery). */
    robot_ids?: number[]
  }): Promise<{ state: 'syncing' | 'ready'; pending: number }> => {
    const res = (await __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/gausium-normalized-sync-status/',
      query: toNormalizedRobotScopeQuery({ ...params } as Record<string, unknown>) as any,
    })) as any
    return res as { state: 'syncing' | 'ready'; pending: number }
  },

  /** Incremental GS normalized rows after cursor `(updated_at|event_id)` for this date window. */
  fetchGausiumNormalizedDelta: async (params: {
    start_date: string
    end_date: string
    robot_sn?: string
    /** If set, takes precedence over robot_sn (see toNormalizedRobotScopeQuery). */
    robot_ids?: number[]
    cursor?: string
    limit?: number
  }): Promise<{ results: any[]; next_cursor: string; count: number }> => {
    if (params.robot_ids && params.robot_ids.length > 50) {
      const chunks = chunk(params.robot_ids, 25)
      const responses = await Promise.all(
        chunks.map((idsChunk: number[]) =>
          __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/gausium-normalized-delta/',
            query: {
              start_date: params.start_date,
              end_date: params.end_date,
              robot_id: idsChunk,
              cursor: params.cursor,
              limit: params.limit,
            } as any,
          })
        )
      )
      const merged = (responses as any[]).flatMap(r => (r?.results ?? []) as any[])
      const byEventId = new Map<string, any>()
      for (const row of merged) {
        const id = String(row?.event_id ?? '')
        if (!id) continue
        byEventId.set(id, row)
      }
      const results = Array.from(byEventId.values())
      const next_cursor = buildDeltaCursor(results) || (params.cursor ?? '')
      return { results, next_cursor, count: results.length }
    }

    const res = (await __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/gausium-normalized-delta/',
      query: toNormalizedRobotScopeQuery({ ...params } as Record<string, unknown>) as any,
    })) as any
    return res as { results: any[]; next_cursor: string; count: number }
  },

  /** Queue Celery per-robot GS normalized gap fill (202); does not block on vendor API. */
  queueGausiumNormalizedGapRefresh: async (params: {
    start_date: string
    end_date: string
    robot_sn?: string
    /** If set (incl. empty), body sends only robot_ids — robot_sn is ignored. */
    robot_ids?: number[]
  }): Promise<{ queued: number; robot_ids: number[]; deduped?: boolean }> => {
    const body: Record<string, unknown> = {
      start_date: params.start_date,
      end_date: params.end_date,
    }
    if (params.robot_ids !== undefined) {
      body.robot_ids = params.robot_ids
    } else if (params.robot_sn) {
      body.robot_sn = params.robot_sn
    }
    const res = (await __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/gausium-normalized-gap-refresh/',
      body,
      mediaType: 'application/json',
    })) as any
    return res as { queued: number; robot_ids: number[]; deduped?: boolean }
  },

  postGausiumRemoteCommand: async (
    robotId: number,
    body: {
      kind: 'start_task' | 'pause_task' | 'resume_task' | 'stop_task' | 'cross_navigate' | 'emergency_stop'
      map_name?: string
      task_name?: string
      cleaning_mode?: string
      loop?: boolean
      loop_count?: number
      start_delay?: number
      navigate_point?: string
      emergency_enabled?: boolean
    }
  ): Promise<Record<string, unknown>> => {
    const response = await fetch(`/api/main/robot/${robotId}/gausium-remote-command/`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!response.ok) {
      const err = (await response.json().catch(() => ({}))) as { detail?: string; message?: string }
      throw new Error(err.detail || err.message || `Gausium command failed (${response.status})`)
    }
    return (await response.json()) as Record<string, unknown>
  },

  /**
   * Trigger lazy preview download for a single Gausium floor map.
   * - returns the (possibly updated) row + `ensure_status`;
   * - throws on 4xx/5xx (incl. 502 'no_uri'/'download_failed') so SPA can render the unavailable stub.
   */
  ensureGausiumMapPreview: async (
    mapId: number,
    options?: { force?: boolean }
  ): Promise<{
    id: number
    robot_id: number
    vendor_map_id: string
    map_name: string
    preview_url: string | null
    preview_fetched_at: string | null
    ensure_status: 'cached' | 'downloaded' | 'no_uri' | 'download_failed' | 'disabled'
  }> => {
    const query: Record<string, any> = {}
    if (options?.force) query.force = '1'
    const response: any = await __request(OpenAPI, {
      method: 'POST',
      url: '/api/main/gausium-maps/{id}/ensure-preview/',
      path: { id: mapId },
      query,
    })
    return response
  },

  fetchGausiumMaps: async (
    robotIds?: number[],
    search?: string,
    page?: number,
    pageSize?: number,
    mapId?: number
  ): Promise<{
    total: number
    results: Array<{
      id: number
      robot_id: number
      robot_sn: string
      robot_type?: string
      robot_type_display?: string
      vendor_map_id: string
      map_version: string
      map_name: string
      subareas_json: any
      preview_url: string | null
      preview_fetched_at: string | null
      synced_at: string | null
      geo_point?: { type: string; coordinates: number[] } | null
      charging_station_pose?: Record<string, unknown> | null
    }>
  }> => {
    const query: Record<string, any> = {}
    if (robotIds?.length) query.robot_id = robotIds
    if (mapId != null) query.map_id = mapId
    if (search?.trim()) query.search = search.trim()
    if (page != null && pageSize != null) {
      query.page = page
      query.page_size = pageSize
    }
    const response: any = await __request(OpenAPI, {
      method: 'GET',
      url: '/api/main/gausium-maps/',
      query,
    })
    const results = Array.isArray(response?.results) ? response.results : []
    const total = Number(response?.total ?? response?.count ?? 0) || 0
    return {
      total,
      results,
    }
  },
  async fetchRobotMaps(
    page?: number,
    pageSize?: number,
    search?: string,
    facilityId?: number,
    robotId?: number
  ): Promise<{ results: IRobotMap[]; count: number }> {
    try {
      const params: any = { page, page_size: pageSize, search, facility_id: facilityId, ordering: '-created_at' }
      if (robotId != null) {
        params.robot_id = robotId
      }
      const response: any = await __request(OpenAPI, {
        method: 'GET',
        url: '/api/main/robot-map/',
        query: params,
      })
      return {
        results: response.results ? response.results.map((mapRaw: any) => normalizeRobotMap(mapRaw)) : [],
        count: response.total || response.count || 0,
      }
    } catch (error) {
      console.error('Error fetching robot maps:', error)
      throw error
    }
  },
  async fetchRobotMap(id: number): Promise<IRobotMap> {
    try {
      const response: any = await __request(OpenAPI, {
        method: 'GET',
        url: `/api/main/robot-map/${id}/`,
      })
      return normalizeRobotMap(response)
    } catch (error) {
      console.error('Error fetching robot map:', error)
      throw error
    }
  },
  async deleteRobotMap(id: number): Promise<void> {
    try {
      await __request(OpenAPI, {
        method: 'DELETE',
        url: `/api/main/robot-map/${id}/`,
      })
    } catch (error) {
      console.error('Error deleting robot map:', error)
      throw error
    }
  },
  async renameRobotMap(id: number, name: string, isBaseMap?: boolean): Promise<void> {
    try {
      const body: any = { name }
      if (isBaseMap !== undefined) {
        body.is_base_map = isBaseMap
      }
      await __request(OpenAPI, {
        method: 'PATCH',
        url: `/api/main/robot-map/${id}/`,
        body,
      })
    } catch (error) {
      console.error('Error renaming robot map:', error)
      throw error
    }
  },
  async downloadRobotMap(id: number, name: string): Promise<void> {
    try {
      const token =
        typeof OpenAPI.TOKEN === 'function'
          ? await (OpenAPI.TOKEN as (o?: unknown) => Promise<string | undefined> | string | undefined)({})
          : OpenAPI.TOKEN
      const base = (OpenAPI.BASE || '').replace(/\/$/, '')
      const url = `${base}/api/main/robot-map/${id}/download/`
      const headers: HeadersInit = {}
      if (token) (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`

      const res = await fetch(url, { headers })
      if (!res.ok) {
        throw new Error(`Download failed: ${res.statusText}`)
      }
      const blob = await res.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.setAttribute('download', `${name}.tif`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      console.error('Error downloading robot map:', error)
      throw error
    }
  },
  async uploadRobotMap(id: number, file: File): Promise<IRobotMap> {
    const formData = new FormData()
    formData.append('file', file)

    const token =
      typeof OpenAPI.TOKEN === 'function'
        ? await (OpenAPI.TOKEN as (o?: unknown) => Promise<string | undefined> | string | undefined)({})
        : OpenAPI.TOKEN
    const base = (OpenAPI.BASE || '').replace(/\/$/, '')
    const url = `${base}/api/main/robot-map/${id}/upload/`
    const headers: HeadersInit = {}
    if (token) (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    })

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.statusText}`)
    }
    const data = await response.json()
    return normalizeRobotMap(data)
  },
  async restoreRobotMap(id: number): Promise<IRobotMap> {
    try {
      const response: any = await __request(OpenAPI, {
        method: 'POST',
        url: `/api/main/robot-map/${id}/restore/`,
      })
      return normalizeRobotMap(response)
    } catch (error) {
      console.error('Error restoring robot map:', error)
      throw error
    }
  },
  async eraseRobotMapObstacles(id: number, polygons: any[]): Promise<IRobotMap> {
    try {
      const response: any = await __request(OpenAPI, {
        method: 'POST',
        url: `/api/main/robot-map/${id}/erase-obstacles/`,
        body: { polygons },
      })
      return normalizeRobotMap(response)
    } catch (error) {
      console.error('Error erasing robot map obstacles:', error)
      throw error
    }
  },
  async fetchFacilityLots(facilityId: number): Promise<any[]> {
    try {
      const response: any = await __request(OpenAPI, {
        method: 'GET',
        url: '/api/main/robot-lot/',
        query: { facility: facilityId, page_size: 1000 },
      })
      return response.results || []
    } catch (error) {
      console.error('Error fetching facility lots:', error)
      return []
    }
  },
}
